'use client';
import { env } from '@/constant/configs';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

interface ActiveCampaignEmbedProps {
  formId?: number;
  domain?: string;
}

const ActiveCampaignEmbed = ({
  formId = 29,
  domain = env.activeHostedEmbedURL,
}: ActiveCampaignEmbedProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const t = useTranslations();

  useEffect(() => {
    let isMounted = true;
    let script: HTMLScriptElement | null = null;

    const loadFormInContainer = async () => {
      try {
        if (!containerRef.current) return;

        setIsLoading(true);
        setError(null);

        // Clear container first
        containerRef.current.innerHTML = '';

        // Create a wrapper div for the form
        const formWrapper = document.createElement('div');
        formWrapper.className = `_form_${formId}`;

        // Create script element
        script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = `${domain}?id=${formId}&t=${Date.now()}`;

        // Add both wrapper and script to our container
        containerRef.current.appendChild(formWrapper);
        containerRef.current.appendChild(script);

        // Wait for script to load and form to initialize
        await new Promise<void>((resolve, reject) => {
          if (!script) {
            reject(new Error('Script element not created'));
            return;
          }

          script.onload = () => {
            // ActiveCampaign should populate the _form_X div
            // Check periodically if form content has appeared
            let attempts = 0;
            const maxAttempts = 20;

            const checkFormContent = () => {
              if (!isMounted || !containerRef.current) {
                reject(new Error('Component unmounted'));
                return;
              }

              attempts++;
              const formDiv = containerRef.current.querySelector(
                `._form_${formId}`
              );

              // Check if form has content (either children or innerHTML)
              const hasContent =
                formDiv &&
                (formDiv.children.length > 0 ||
                  formDiv.innerHTML.trim() !== '');

              if (hasContent) {
                resolve();
              } else if (attempts >= maxAttempts) {
                reject(new Error('Form content failed to load'));
              } else {
                setTimeout(checkFormContent, 250);
              }
            };

            // Start checking after a small delay
            setTimeout(checkFormContent, 500);
          };

          script.onerror = () => {
            reject(new Error('Failed to load ActiveCampaign script'));
          };
        });

        if (isMounted) {
          setIsLoaded(true);
          setIsLoading(false);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err.message);
          setIsLoading(false);
        }
      }
    };

    loadFormInContainer();

    return () => {
      isMounted = false;

      // Clean up: remove script and clear container
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }

      // Also clean up any orphaned forms that might have been created elsewhere
      const orphanedForms = document.querySelectorAll(`._form_${formId}`);
      orphanedForms.forEach((form) => {
        // Only remove if it's not inside our container
        if (!containerRef.current?.contains(form)) {
          form.remove();
        }
      });
    };
  }, [formId, domain]);

  const retryLoad = () => {
    setError(null);
    setIsLoading(true);
    setIsLoaded(false);

    // Trigger re-run of useEffect
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }

    // Force re-render by updating a state that will trigger useEffect
    window.location.reload();
  };

  if (error) {
    return (
      <div
        style={{
          padding: '20px',
          border: '1px solid #e74c3c',
          borderRadius: '8px',
          backgroundColor: '#fdf2f2',
          color: '#e74c3c',
          textAlign: 'center',
        }}
      >
        <p>{t('common.fail_process')}</p>
        <button
          onClick={retryLoad}
          style={{
            padding: '8px 16px',
            backgroundColor: '#e74c3c',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginTop: '10px',
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        minHeight: isLoading ? '120px' : 'auto',
        width: '100%',
      }}
    >
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            color: '#666',
            fontSize: '14px',
            zIndex: 10,
          }}
        >
          <div
            style={{
              width: '20px',
              height: '20px',
              border: '2px solid #f3f3f3',
              borderTop: '2px solid #3498db',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          {t('common.loading')}...
        </div>
      )}

      {/* This container will hold both the form div and script */}
      <div
        ref={containerRef}
        style={{
          width: '100%',
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      />

      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default ActiveCampaignEmbed;
